import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { TCompanyForm } from "@/features/company/types/companyForm";

/** Business classification fields that pair with a specific-industry selection. */
type TBusinessClassificationField =
  | "companySpecificBusinessClassificationIds1"
  | "companySpecificBusinessClassificationIds2"
  | "companySpecificBusinessClassificationIds3";

/**
 * Keeps a 業務区分 (business classification) multi-select field in sync with the industry selected in the same row:
 * - options are derived from the selected industry;
 * - if the industry has exactly one classification, it is auto-selected ([onlyValue]) and the field is shown as
 *   readonly (value assigned & visible, no interaction) — not disabled;
 * - the field is disabled only while no industry is selected;
 * - any selected values that no longer belong to the selected industry (industry changed/cleared) are dropped,
 *   collapsing to [] when no industry is selected.
 *
 * Values are compared as strings because the form stores string ids while option values may be numbers.
 */
export const useBusinessClassificationSync = (industryId: number | null | undefined, fieldName: TBusinessClassificationField) => {
  const { control, setValue } = useFormContext<TCompanyForm>();
  const { getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry, isLoading } = useGetCompanySpecificIndustryList();
  const selectedValue = useWatch({ control, name: fieldName });

  const options = useMemo(
    () => (industryId ? getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry(industryId) : []),
    [industryId, getCompanySpecificBusinessClassificationListOptionByCompanySpecificIndustry],
  );

  useEffect(() => {
    if (isLoading) return;
    const current = Array.isArray(selectedValue) ? selectedValue : [];
    if (options.length === 1) {
      const onlyValue = String(options[0].value);
      if (current.length !== 1 || String(current[0]) !== onlyValue) setValue(fieldName, [onlyValue]);
      return;
    }
    const validValues = current.filter((value) => options.some((opt) => String(opt.value) === String(value)));
    if (validValues.length !== current.length) setValue(fieldName, validValues);
  }, [isLoading, selectedValue, options, setValue, fieldName]);

  return { options, disabled: !industryId, readonly: options.length === 1 };
};
